import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ExternalLink, Play, ChevronRight, CheckCircle2 } from 'lucide-react';
import SlideFoto from '../SlideFoto';
import { tipo, STILI, idYoutube, contoFinito } from './riquadri';
import { etichetta as nomeTessera, COLORI as COLORI_TESSERA, fineValidita, giorniAllaScadenza } from '../../utils/tessere';
import './DashboardCliente.css';

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/api$/, '');
const euro = (n) => `${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const giornoBello = (g) => ({ LUNEDI: 'Lunedì', MARTEDI: 'Martedì', MERCOLEDI: 'Mercoledì', GIOVEDI: 'Giovedì', VENERDI: 'Venerdì', SABATO: 'Sabato', DOMENICA: 'Domenica' }[g] || g);
const titoloCaso = (s) => String(s || '').toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase());
const dataBreve = (d) => new Date(d).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
const STATO_ORDINE = { BOZZA: '#8E8E93', CONFERMATO: '#3B82F6', 'IN LAVORAZIONE': '#F59E0B', PRONTO: '#8B5CF6', CONSEGNATO: '#7C6BD6', PAGATO: '#22C55E', ANNULLATO: '#EF4444' };
const STATO_FIPAV = { RICHIESTA: 'In attesa', PRESA_IN_CARICO: 'Presa in carico', CONFERMATA: 'Iscritti', SEGNALATA: 'Segnalata' };

// Bottone dentro un riquadro: pagina del portale o sito esterno (nuova scheda)
function Bottone({ w, chiaro, anteprima }) {
  const navigate = useNavigate();
  if (!w.link_url) return null;
  const testo = w.link_testo || 'Scopri di più';
  const cls = `dc-btn${chiaro ? ' chiaro' : ''}`;
  if (w.link_url.startsWith('/')) {
    return <button type="button" className={cls} onClick={() => !anteprima && navigate(w.link_url)}>{testo} <ChevronRight size={16} /></button>;
  }
  return <a className={cls} href={anteprima ? undefined : w.link_url} target="_blank" rel="noopener noreferrer">{testo} <ExternalLink size={14} /></a>;
}

function Testata({ icona, titolo, link, anteprima }) {
  const navigate = useNavigate();
  return (
    <div className="dc-testata">
      <span className="dc-icona">{icona}</span>
      <span className="dc-titolo">{titolo}</span>
      {link && <button type="button" className="dc-vai" onClick={() => !anteprima && navigate(link)} aria-label="Apri">Apri <ChevronRight size={14} /></button>}
    </div>
  );
}

function Conto({ w }) {
  const [adesso, setAdesso] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setAdesso(new Date()), 30000); return () => clearInterval(t); }, []);
  const quando = new Date(w.contenuto || '');
  if (isNaN(quando)) return <div className="dc-vuoto">Data dell'evento da impostare</div>;
  const diff = quando - adesso;
  const g = Math.floor(diff / 86400000), o = Math.floor(diff / 3600000) % 24, m = Math.floor(diff / 60000) % 60;
  const stessoGiorno = quando.toDateString() === adesso.toDateString();
  return (
    <div className="dc-conto">
      {diff > 0 ? (
        <div className="dc-conto-numeri">
          {g > 0 && <div><b>{g}</b><span>{g === 1 ? 'giorno' : 'giorni'}</span></div>}
          <div><b>{o}</b><span>{o === 1 ? 'ora' : 'ore'}</span></div>
          <div><b>{m}</b><span>min</span></div>
        </div>
      ) : <div className="dc-conto-ora">{stessoGiorno ? 'È oggi! 🎉' : contoFinito(w, adesso) ? 'Evento concluso' : 'Ci siamo! 🎉'}</div>}
      <div className="dc-conto-data">{quando.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}{quando.getHours() || quando.getMinutes() ? ` · ore ${quando.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
    </div>
  );
}

function Video({ id, titolo }) {
  const [parti, setParti] = useState(false);
  if (parti) return <iframe className="dc-video" src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`} title={titolo || 'Video'} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />;
  return (
    <button type="button" className="dc-video-avvia" onClick={() => setParti(true)}>
      <span className="dc-play"><Play size={28} fill="currentColor" /></span>
      <span>Guarda il video</span>
      <small>Si apre il lettore di YouTube</small>
    </button>
  );
}

// Un riquadro. dati = dati personali di chi guarda (o di esempio in anteprima).
// Restituisce null se un riquadro automatico non ha niente da mostrare.
export default function Riquadro({ w, dati = {}, anteprima = false }) {
  const t = tipo(w.tipo);
  const stile = w.stile ? STILI[w.stile] : null;
  const cls = `dc-r dc-s${Math.min(3, w.col_span || 1)}${w.row_span > 1 ? ' dc-alto' : ''}`;
  const titolo = w.titolo || t.titolo;

  if (w.tipo === 'BANNER') {
    const img = w.contenuto ? `${API_BASE}${w.contenuto}` : null;
    return (
      <div className={`${cls} dc-banner`} style={img ? undefined : { background: (stile || STILI.arancio).sfondo }}>
        {img && <img src={img} alt={w.titolo || ''} />}
        {(w.titolo || w.sottotitolo || w.link_url) && (
          <div className={`dc-banner-testo${img ? ' su-foto' : ''}`}>
            {w.titolo && <h2>{w.titolo}</h2>}
            {w.sottotitolo && <p>{w.sottotitolo}</p>}
            <Bottone w={w} anteprima={anteprima} />
          </div>
        )}
        {!img && !w.titolo && <div className="dc-vuoto chiaro">Carica una foto</div>}
      </div>
    );
  }

  if (w.tipo === 'CAROSELLO') {
    const foto = (Array.isArray(w.immagini) ? w.immagini : []).map(f => `${API_BASE}${f}`);
    return (
      <div className={`${cls} dc-banner`}>
        {foto.length ? <div className="dc-foto"><SlideFoto foto={foto} alt={w.titolo || 'Foto'} fit="cover" tastiera={false} ogni={5000} /></div> : <div className="dc-vuoto chiaro">Aggiungi le foto</div>}
        {(w.titolo || w.sottotitolo || w.link_url) && (
          <div className="dc-banner-testo su-foto in-alto">
            {w.titolo && <h2>{w.titolo}</h2>}
            {w.sottotitolo && <p>{w.sottotitolo}</p>}
            <Bottone w={w} anteprima={anteprima} />
          </div>
        )}
      </div>
    );
  }

  if (w.tipo === 'TESTO' || w.tipo === 'CONTO') {
    if (w.tipo === 'CONTO' && !anteprima && contoFinito(w)) return null;
    const pieno = !!stile;
    return (
      <div className={`${cls} dc-avviso${pieno ? ' pieno' : ''}`} style={pieno ? { background: stile.sfondo, color: stile.testo } : undefined}>
        {titolo && <div className="dc-avviso-titolo">{titolo}</div>}
        {w.tipo === 'CONTO' ? <Conto w={w} /> : <div className="dc-avviso-testo">{w.contenuto}</div>}
        {w.tipo === 'CONTO' && w.sottotitolo && <div className="dc-avviso-testo piccolo">{w.sottotitolo}</div>}
        <div className="dc-piede"><Bottone w={w} chiaro={pieno && stile.testo === '#fff'} anteprima={anteprima} /></div>
      </div>
    );
  }

  if (w.tipo === 'FILE') {
    return (
      <div className={`${cls} dc-carta`}>
        <Testata icona="📄" titolo={titolo || 'Documento'} anteprima={anteprima} />
        {w.sottotitolo && <div className="dc-testo">{w.sottotitolo}</div>}
        <div className="dc-file-nome">{w.file_nome || (w.contenuto ? 'Documento' : 'Nessun file caricato')}</div>
        <div className="dc-piede">
          {w.contenuto && <a className="dc-btn" href={anteprima ? undefined : `${API_BASE}${w.contenuto}`} download={w.file_nome || true}><Download size={15} /> Scarica</a>}
        </div>
      </div>
    );
  }

  if (w.tipo === 'URL') {
    const yt = idYoutube(w.contenuto);
    return (
      <div className={`${cls} dc-carta${yt ? ' dc-con-video' : ''}`}>
        <Testata icona={yt ? '🎬' : '🔗'} titolo={titolo || (yt ? 'Video' : 'Link')} anteprima={anteprima} />
        {w.sottotitolo && <div className="dc-testo">{w.sottotitolo}</div>}
        {yt ? (anteprima ? <div className="dc-video-avvia finto"><span className="dc-play"><Play size={28} fill="currentColor" /></span><span>Guarda il video</span></div> : <Video id={yt} titolo={titolo} />) : (
          <div className="dc-piede">
            {w.contenuto && <a className="dc-btn" href={anteprima ? undefined : w.contenuto} target="_blank" rel="noopener noreferrer">{w.link_testo || 'Apri il link'} <ExternalLink size={14} /></a>}
          </div>
        )}
      </div>
    );
  }

  // ── automatici ──────────────────────────────────────────────────────────────────────
  if (w.tipo === 'CORSO') {
    const corsi = dati.corso || [];
    if (!corsi.length) return null;
    return (
      <div className={`${cls} dc-avviso pieno dc-corso`} style={{ background: STILI.scuro.sfondo, color: '#fff' }}>
        <Testata icona="🏐" titolo={titolo} anteprima={anteprima} />
        {corsi.slice(0, 3).map((c, i) => (
          <div key={i} className="dc-corso-riga">
            <div className="dc-corso-quando">{giornoBello(c.giorno)} <span>· {c.orario}</span></div>
            <div className="dc-corso-dove">{titoloCaso(c.sede)}{c.campo ? ` · ${titoloCaso(c.campo)}` : ''}</div>
            {c.allenatore && <div className="dc-corso-all">Allenatore: <b>{titoloCaso(c.allenatore)}</b></div>}
          </div>
        ))}
        <div className="dc-nota">{corsi[0].ciclo}{corsi[0].data_fine ? ` · fino al ${new Date(corsi[0].data_fine).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}` : ''}</div>
      </div>
    );
  }

  if (w.tipo === 'PAGAMENTI') {
    const p = dati.pagamenti || [];
    if (!p.length) return null;
    const totale = p.reduce((s, x) => s + (x.totale || 0), 0), pagato = p.reduce((s, x) => s + (x.pagato || 0), 0);
    const residuo = p.reduce((s, x) => s + (x.residuo || 0), 0);
    const perc = totale ? Math.min(100, Math.round((pagato / totale) * 100)) : 100;
    return (
      <div className={`${cls} dc-carta`}>
        <Testata icona="💶" titolo={titolo} link="/cliente/ricevute" anteprima={anteprima} />
        {residuo > 0.004 ? (
          <>
            <div className="dc-numero">{euro(residuo)}<small>da saldare</small></div>
            <div className="dc-barra"><span style={{ width: `${perc}%` }} /></div>
            <div className="dc-testo piccolo">Pagati {euro(pagato)} su {euro(totale)} · {p[0].ciclo}</div>
          </>
        ) : (
          <div className="dc-ok"><CheckCircle2 size={34} /> <div><b>Tutto pagato</b><span>{p[0].ciclo}</span></div></div>
        )}
      </div>
    );
  }

  if (w.tipo === 'TESSERA') {
    const tes = dati.tessere || [];
    if (!tes.length) return null;
    // la più recente per federazione
    const ultime = Object.values(tes.reduce((m, x) => { if (!m[x.federazione] || x.stagione > m[x.federazione].stagione) m[x.federazione] = x; return m; }, {}));
    return (
      <div className={`${cls} dc-carta`}>
        <Testata icona="🪪" titolo={titolo} link="/cliente/tessera" anteprima={anteprima} />
        <div className="dc-tessere">
          {ultime.map(x => {
            const fine = fineValidita(x), gg = giorniAllaScadenza(x);
            return (
              <div key={x.id} className="dc-tessera" style={{ '--c': COLORI_TESSERA[x.federazione] || '#888' }}>
                <div className="dc-tessera-fed">{nomeTessera(x.federazione)}</div>
                <div className="dc-tessera-num">n° {x.numero_tessera}</div>
                {fine && <div className={`dc-tessera-sc${gg < 0 ? ' scaduta' : gg <= 30 ? ' presto' : ''}`}>{gg < 0 ? 'Scaduta' : `valida fino al ${fine.split('-').reverse().join('/')}`}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (w.tipo === 'FIPAV') {
    const f = dati.fipav;
    if (!f?.punteggio) return null;
    const ass = f.punteggio.punti?.find(x => x.categoria === 'Assoluto') || f.punteggio.punti?.[0];
    const si = (f.punteggio.livelli || []).filter(l => l.stato === 'SI').map(l => l.nome);
    const inCorso = (f.richieste || []).filter(r => r.in_corso);
    return (
      <div className={`${cls} dc-avviso pieno dc-fipav`} style={{ background: STILI.arancio.sfondo, color: '#fff' }}>
        <Testata icona="🏆" titolo={titolo} link="/cliente/fipav" anteprima={anteprima} />
        <div className="dc-numero chiaro">{ass ? ass.punti_entry ?? ass.punti : 0}<small>punti entry</small></div>
        {ass?.posizione && <div className="dc-testo piccolo">{ass.posizione}° in classifica {ass.categoria}</div>}
        {si.length > 0 && <div className="dc-chip-riga">{si.map(n => <span key={n} className="dc-chip">{n}</span>)}</div>}
        {inCorso.length > 0 && (
          <div className="dc-nota">{inCorso.length === 1
            ? `Iscrizione: ${inCorso[0].torneo?.nome || inCorso[0].torneo?.luogo} · ${STATO_FIPAV[inCorso[0].stato] || inCorso[0].stato}`
            : `${inCorso.length} iscrizioni in corso`}</div>
        )}
      </div>
    );
  }

  if (w.tipo === 'CAMPI') {
    const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
    const prossime = (dati.campi || []).filter(p => p.stato === 'CONFERMATA' && new Date(p.data) >= oggi).sort((a, b) => new Date(a.data) - new Date(b.data) || String(a.ora_inizio).localeCompare(b.ora_inizio));
    if (!prossime.length) return null;
    const p = prossime[0];
    return (
      <div className={`${cls} dc-carta`}>
        <Testata icona="📅" titolo={titolo} link="/cliente/campi" anteprima={anteprima} />
        <div className="dc-corso-quando nero">{dataBreve(p.data)} <span>· {p.ora_inizio}–{p.ora_fine}</span></div>
        <div className="dc-testo">{p.campo?.nome}{p.campo?.sede?.nome ? ` · ${p.campo.sede.nome}` : ''}</div>
        {prossime.length > 1 && <div className="dc-testo piccolo">e altre {prossime.length - 1} prenotazioni</div>}
      </div>
    );
  }

  if (w.tipo === 'ORDINI') {
    const o = (dati.ordini || []).filter(x => x.stato !== 'BOZZA');
    if (!o.length) return null;
    return (
      <div className={`${cls} dc-carta`}>
        <Testata icona="🛍️" titolo={titolo} link="/cliente/ordini" anteprima={anteprima} />
        <div className="dc-ordini">
          {o.slice(0, w.row_span > 1 ? 6 : 3).map(x => {
            const c = STATO_ORDINE[x.stato] || '#8E8E93';
            return (
              <div key={x.id} className="dc-ordine">
                <div><b>{x.numero_ordine}</b><span>{new Date(x.created_at).toLocaleDateString('it-IT')}</span></div>
                <div className="dc-ordine-dx"><span className="dc-pill" style={{ color: c, background: `${c}1A` }}>{x.stato}</span><b>{euro(x.totale)}</b></div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
}

// Griglia: 3 colonne sul computer, 2 a metà, 1 sul telefono (container query: vale anche in anteprima)
export function GrigliaRiquadri({ widgets, dati, anteprima }) {
  return (
    <div className="dc">
      <div className="dc-griglia">
        {widgets.map(w => <Riquadro key={w.id} w={w} dati={dati} anteprima={anteprima} />)}
      </div>
    </div>
  );
}
